import type { Data } from '@dnd-kit/abstract'
import { SortableDraggable } from '@dnd-kit/dom/sortable'
import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'

import { Flex } from '@/components/ui/flex'

import { BlockCard } from '../block-card'
import { SortableBlockItem } from '../block-item'
import { MAX_ENABLED_SHORTCUTS } from '../home-screen.constants'
import { HomepageConfigResponse, ShortcutType } from '../home-screen.types'
import { isShortcutEnabled } from '../home-screen.utils'

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>

interface ShortcutsBlockProps {
  config: HomepageConfigResponse
  order: ShortcutType[]
  shortcutLabels: Record<ShortcutType, string>
  disabled: boolean
  // The Shortcuts widget itself is off - the list stays visible (grayed out) instead of
  // disappearing, but nothing in it can be toggled or dragged.
  locked: boolean
  onToggle: (type: ShortcutType) => void
  onReorder: (sourceIndex: number, targetIndex: number) => void
}

export const ShortcutsBlock = ({
  config,
  order,
  shortcutLabels,
  disabled,
  locked,
  onToggle,
  onReorder,
}: ShortcutsBlockProps) => {
  const { t } = useTranslation(['home-screen'])
  const isCapReached = config.shortcuts.length >= MAX_ENABLED_SHORTCUTS

  const handleDragEnd: DragEndHandler = (event) => {
    if (locked) return
    const { canceled, operation } = event
    if (canceled) return

    const { source } = operation
    if (!source || !isSortable(source)) return

    const sortableSource = source as SortableDraggable<Data>
    const sourceIndex = sortableSource.initialIndex
    const targetIndex = sortableSource.index
    if (sourceIndex === targetIndex) return

    onReorder(sourceIndex, targetIndex)
  }

  return (
    <BlockCard
      locked={locked}
      title={t('home-screen:labels.shortcutsConfigurationHeader')}
      tooltip={t('home-screen:tooltips.shortcutsConfiguration', { max: MAX_ENABLED_SHORTCUTS })}
    >
      <Flex direction="column" gap={8}>
        <DragDropProvider onDragEnd={handleDragEnd}>
          {order.map((type, index) => {
            const enabled = isShortcutEnabled(config, type)
            const capBlocked = !locked && !enabled && isCapReached

            return (
              <SortableBlockItem
                key={type}
                checked={enabled}
                disabled={disabled}
                id={`shortcut-${type}`}
                index={index}
                label={shortcutLabels[type]}
                locked={locked || capBlocked}
                tooltip={
                  capBlocked
                    ? t('home-screen:messages.shortcutsCapHint', { max: MAX_ENABLED_SHORTCUTS })
                    : undefined
                }
                onToggle={() => onToggle(type)}
              />
            )
          })}
        </DragDropProvider>
      </Flex>
    </BlockCard>
  )
}
